import { createTheme } from '@mui/material/styles';

import baseline, { baselinePalette } from '../baseline';
import { sisGray9, activeIconColor } from 'lib/v2/colors';

export const primary = '#64b5f6'; // Lighter blue for better visibility on dark
export const secondary = '#ce93d8'; // Purple for dark mode (less jarring than pink)
const backgroundDefault = '#2c2c2c'; // Softer dark gray (not pure black)
const backgroundPaper = sisGray9; // #3b3b3b - Lighter gray for content areas
const backgroundElevated = '#424242'; // Even lighter for elevated surfaces

const defaultTheme = createTheme();
export const theme = createTheme({
  ...baseline,
  palette: {
    ...baselinePalette,
    primary: { main: primary, contrastText: '#ffffff' },
    secondary: { main: secondary, contrastText: '#ffffff' },
    info: { main: '#29b6f6', dark: '#4fc3f7', contrastText: '#ffffff' },
    alert: { main: '#ef5350', contrastText: '#ffffff' },
    success: { main: '#66bb6a', contrastText: '#ffffff' },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
      disabled: '#6b6b6b',
    },
    background: {
      default: backgroundDefault,
      paper: backgroundPaper,
    },
    divider: 'rgba(255, 255, 255, 0.15)',
    link: {
      main: primary,
    },
    appbar: defaultTheme.palette.augmentColor({
      color: { main: backgroundPaper, border: 'rgba(255, 255, 255, 0.1)' },
      name: 'appbar',
    }),
    navbar: defaultTheme.palette.augmentColor({
      color: {
        main: backgroundPaper,
        border: 'rgba(255, 255, 255, 0.1)',
        icon: '#b0b0b0',
        activeIcon: activeIconColor,
      },
      name: 'navbar',
    }),
    tableAction: defaultTheme.palette.augmentColor({
      color: { main: '#64b5f6', icon: '#b0b0b0' },
      name: 'tableAction',
    }),
    tableHeader: defaultTheme.palette.augmentColor({
      color: { main: '#242424' },
      name: 'tableHeader',
    }),
    backgroundBasic: defaultTheme.palette.augmentColor({
      color: { main: backgroundElevated },
      name: 'backgroundBasic',
    }),
    mode: 'dark',
  },
  components: {
    ...baseline.components,
    MuiCssBaseline: {
      styleOverrides: {
        a: {
          color: primary,
          '&:visited': {
            color: primary,
          },
        },
        '.Manage-route': {
          color: '#b0b0b0 !important',
        },
        '.Manage-route.is-active': {
          color: '#90caf9 !important',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            color: '#ffffff',
          },
          '& .MuiDataGrid-row': {
            backgroundColor: '#1e1e1e',
            '&:nth-of-type(odd)': {
              backgroundColor: '#222222',
            },
          },
        },
      },
    },
    MuiDataGridPro: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            color: '#ffffff',
          },
          '& .MuiDataGrid-row': {
            backgroundColor: '#1e1e1e',
            '&:nth-of-type(odd)': {
              backgroundColor: '#222222',
            },
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: backgroundElevated,
          color: '#ffffff !important',
          '&:hover': {
            backgroundColor: '#4a4a4a',
          },
          '&.Mui-focused': {
            backgroundColor: backgroundElevated,
          },
          '&.Mui-disabled': {
            backgroundColor: '#353535',
            color: '#6b6b6b',
            '& button': {
              display: 'none',
            },
          },
        },
        input: {
          color: '#ffffff !important',
          WebkitTextFillColor: '#ffffff !important',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primary,
          },
        },
        input: {
          color: '#ffffff',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#2e2e2e',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
        input: {
          color: '#ffffff',
          '&::placeholder': {
            color: '#b0b0b0',
            opacity: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#b0b0b0',
          '&.Mui-focused': {
            color: primary,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        input: {
          color: '#ffffff !important',
        },
        tag: {
          color: '#ffffff',
        },
      },
    },
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: primary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
        contained: {
          color: '#ffffff !important',
        },
        containedPrimary: {
          color: '#ffffff !important',
        },
        containedSecondary: {
          color: '#ffffff !important',
        },
        containedInfo: {
          color: '#ffffff !important',
        },
      },
    },
  },
});

export default theme;
